import type { AxiosInstance, AxiosResponse } from "axios";

/**
 * Tags HTTP API — aligned with `TAGS_MANAGER_BACKEND_CONTRACT.md` (v2, source of truth).
 * The shared `useAxiosAuth0()` client defaults to `${REACT_APP_API_URL}/v1`.
 * Reflector V2 routes override `baseURL` to `.../v2` so the Auth0 interceptor stays active.
 *
 * Naming follows the same verb-prefix conventions as
 * `src/ctxReflectorV2/components/Reflector/request/request.tsx` (get / create / update / delete).
 */

// ---------------------------------------------------------------------------
// Mount points (§1.1) — same controller, pick prefix for the active product surface
// ---------------------------------------------------------------------------

export type TagsApiMountPoint = "kontrol-edge" | "reflector-v2" | "ctxr";

/** Mount used by Account → Tags Manager (global catalog on the v1 KE controller). */
export const TAGS_MANAGER_ACCOUNT_API_MOUNT: TagsApiMountPoint = "kontrol-edge";

/** API wire `module` values (§2.4) — use in request bodies / query filters. */
export type TagsApiModule =
  | "kontrol-edge"
  | "reflector-v2"
  | "reflector-v1";

// ---------------------------------------------------------------------------
// Envelope + shared shapes
// ---------------------------------------------------------------------------

export interface TagsApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/** Category embedded in every Tag response (§2.6 / §3.1). */
export interface CategoryApiRow {
  id: number;
  name: string;
}

// ---------------------------------------------------------------------------
// §3 Tag entities
// ---------------------------------------------------------------------------

/**
 * Full tag row as returned by list, create, get-by-id, and update endpoints.
 * `category` is an embedded object (not a string) — FK to the `Categories` table.
 * `color` is the semantic name only (e.g. `"red"`); the frontend maps it to a CSS token.
 */
export interface TagApiRow {
  id: number;
  name: string;
  slug: string;
  /**
   * Numeric owning tenant of the tag. Used by the wizard in edit mode to scope
   * categories (§3.7 self) and the resource catalog (§6) to the tag's own
   * tenant instead of the SideNav lens. Optional on the wire so older cached
   * snapshots / pre-rollout backends keep parsing.
   */
  tenantId?: number;
  description: string | null;
  color: string | null;
  /** Embedded category object; null when the tag is uncategorized. */
  category: CategoryApiRow | null;
  resourceCount: number;
  resourceCountByModule: Partial<Record<TagsApiModule, number>>;
  createdAt: string;
  updatedAt: string;
  /**
   * Owning tenant of the tag. Backend embeds it on list/detail responses
   * so partner-view tables can show the originating customer without an
   * extra fetch. Optional in this type because older cached snapshots
   * (and any pre-rollout backend) may not include it.
   */
  tenant?: { name: string } | null;
  /**
   * `true` when `tag.tenantId !== effectiveTenantId` — i.e. the tag belongs
   * to a tenant different from the one the caller is acting as (a Global
   * Admin browsing the whole system, or a Partner browsing a direct child).
   *
   * Per TAGS-API-CONTRACT §3.1 (Draft v2 → fix/tag-manager-listing) this flag
   * is **informational only**: it does NOT lock edition. A Global Admin or a
   * Partner can still write into a `childTag: true` row (writes follow the
   * same hierarchy at the persistence layer). Use it to drive UI affordances
   * such as a "Customer: X" subtitle, not to disable controls.
   */
  childTag?: boolean;
}

/** @deprecated Use `TagApiRow` directly — list and detail responses now share the same shape. */
export type TagApiDetail = TagApiRow;

export interface TagListPage {
  items: TagApiRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface TagCreateBody {
  name: string;
  description?: string | null;
  color?: string | null;
  /** Integer FK to `Categories.id`. Omit to leave uncategorized; null clears on PATCH. */
  categoryId?: number | null;
}

export type TagPatchBody = Partial<
  Pick<TagApiRow, "name" | "description" | "color"> & { categoryId: number | null }
>;

/** Returned by DELETE on success. Hard-delete: row is gone after this. */
export interface TagDeleteData {
  id: number;
}

// ---------------------------------------------------------------------------
// §3.6 Dashboard summary
// ---------------------------------------------------------------------------

export interface TagsSummaryApiData {
  tagsTotal: number;
  taggedResourcesTotal: number;
  categoriesCount: number;
  unusedTagsCount: number;
}

// ---------------------------------------------------------------------------
// §3.7 / §3.8 Categories
// ---------------------------------------------------------------------------

export interface CreateCategoryBody {
  /** Required, 1–64 chars. Idempotent: returns existing row on case-insensitive match. */
  name: string;
}

// ---------------------------------------------------------------------------
// §4 Resources managed from the tag
// ---------------------------------------------------------------------------

export interface TaggedResourceApiRow {
  id: number;
  tagId: number;
  module: TagsApiModule;
  resourceType: string;
  resourceId: number;
  resourceLabel: string | null;
  /**
   * Live secondary identifier resolved from the resource's own model at read
   * time (e.g. a port's subid), mirroring the §6 catalog's `subtitle`. Always
   * present on this endpoint; `null` when the source row no longer resolves.
   */
  subtitle: string | null;
  createdAt: string;
}

export interface TaggedResourceListPage {
  items: TaggedResourceApiRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface TagResourceReplaceBody {
  resources: Array<{
    module: TagsApiModule;
    resourceType: string;
    resourceId: number;
  }>;
}

// ---------------------------------------------------------------------------
// §5 Resources managed from the resource
// ---------------------------------------------------------------------------

/** Minimal tag row returned from resource-scoped list (§5.1). */
export interface TagSummaryApiRow {
  id: number;
  name: string;
  slug: string;
  color: string | null;
  category: CategoryApiRow | null;
}

export interface ResourceTagsReplaceBody {
  tagIds: number[];
}

// ---------------------------------------------------------------------------
// §6 Catalog (picker)
// ---------------------------------------------------------------------------

/**
 * Note: `status` is intentionally omitted in this sprint (see contract §6).
 * It will be added in a follow-up once each adapter has a confirmed source field.
 *
 * Each row carries the owning tenant via the flat `tenantId` + `tenantName`
 * fields so the FE can render a "Customer" column when the caller is
 * browsing cross-tenant (Partner Account view / Global Admin acting as a
 * partner). The backend always returns these; they're typed optional only
 * to tolerate pre-v2 cached snapshots.
 */
export interface CatalogResourceApiRow {
  id: number;
  label: string;
  subtitle: string | null;
  module: TagsApiModule;
  resourceType: string;
  /** Owning tenant id (numeric). Present on every row in v2 responses. */
  tenantId?: number;
  /** Owning tenant display name. Present on every row in v2 responses. */
  tenantName?: string | null;
}

export interface CatalogListPage {
  items: CatalogResourceApiRow[];
  total: number;
  limit: number;
  offset: number;
}

// ---------------------------------------------------------------------------
// Request config helpers
// ---------------------------------------------------------------------------

function getApiBase(): string {
  return process.env.REACT_APP_API_URL || "http://localhost:3000";
}

/**
 * Path prefix + optional v2 baseURL override (per-call).
 * v1 mounts use the axios instance default base (`.../v1`).
 */
function tagsMountConfig(
  mount: TagsApiMountPoint
): { pathPrefix: string; baseURL?: string } {
  if (mount === "reflector-v2") {
    return { pathPrefix: "/reflector", baseURL: `${getApiBase()}/v2` };
  }
  if (mount === "ctxr") {
    return { pathPrefix: "/ctxr" };
  }
  return { pathPrefix: "/kontroledge" };
}

function mergeConfig(
  mount: TagsApiMountPoint,
  extra?: Record<string, unknown>
): Record<string, unknown> {
  const { baseURL } = tagsMountConfig(mount);
  return {
    ...extra,
    ...(baseURL ? { baseURL } : {}),
  };
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach((k) => {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") {
      (out as Record<string, unknown>)[k as string] = v as unknown;
    }
  });
  return out;
}

// ---------------------------------------------------------------------------
// §3 Tag catalog
// ---------------------------------------------------------------------------

export interface GetTagsListParams {
  search?: string;
  /** Filter by category integer id. */
  categoryId?: number;
  limit?: number;
  offset?: number;
  /**
   * Override the tenant derived from the JWT — required when a Global Admin
   * or Partner is acting on behalf of a child tenant. Same convention used
   * by the resource detail GETs (`fetchPort` etc.). Without it the backend
   * resolves the tenant from the caller's JWT, which doesn't match the
   * tenant the dashboard's "tenant lens" (`state.selection.tenantId`)
   * is pointing at.
   */
  tenantId?: number;
}

/**
 * GET `<prefix>/tags` — paginated tag catalog. §3.1.
 */
export function getTagsList(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  params?: GetTagsListParams
): Promise<AxiosResponse<TagsApiEnvelope<TagListPage>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.get<TagsApiEnvelope<TagListPage>>(`${pathPrefix}/tags`, {
    ...mergeConfig(mount, { params: stripUndefined({ ...params }) }),
  });
}

/**
 * GET `<prefix>/tags/summary` — four dashboard counter cards. §3.6.
 *
 * `tenantId` semantics — see {@link GetTagsListParams}.
 */
export function getTagsSummary(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TagsSummaryApiData>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.get<TagsApiEnvelope<TagsSummaryApiData>>(
    `${pathPrefix}/tags/summary`,
    mergeConfig(mount, { params: stripUndefined({ tenantId }) })
  );
}

/**
 * GET `<prefix>/tags/categories` — all categories for the tenant, sorted by name. §3.7.
 *
 * `tenantId` semantics — see {@link GetTagsListParams}.
 */
export function getCategoriesList(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tenantId?: number,
  scope?: "self"
): Promise<AxiosResponse<TagsApiEnvelope<{ items: CategoryApiRow[] }>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  // `scope=self` lists ONLY the acting tenant's own categories (no hierarchy
  // expansion). The wizard uses it so a tag created under a partner offers
  // only the partner's categories — never a child's, which the backend would
  // reject as cross-tenant on create/update.
  return api.get<TagsApiEnvelope<{ items: CategoryApiRow[] }>>(
    `${pathPrefix}/tags/categories`,
    mergeConfig(mount, { params: stripUndefined({ tenantId, scope }) })
  );
}

/**
 * POST `<prefix>/tags/categories` — idempotent create (returns existing row on case-insensitive match). §3.8.
 *
 * `tenantId` semantics — see {@link GetTagsListParams}. Without it the new
 * category is owned by the caller's tenant; if the user is editing a tag
 * of a child tenant, the FK on `Tags.categoryId` would then mismatch.
 */
export function createCategory(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  body: CreateCategoryBody,
  tenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<CategoryApiRow>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.post<TagsApiEnvelope<CategoryApiRow>>(
    `${pathPrefix}/tags/categories`,
    body,
    mergeConfig(mount, { params: stripUndefined({ tenantId }) })
  );
}

/**
 * POST `<prefix>/tags` — create tag (`slug` computed server-side from `name`). §3.2.
 *
 * `tenantId` overrides the tenant derived from the JWT — required when a
 * Global Admin or Partner is acting on behalf of a child tenant (matches
 * how `fetchPort` and friends pass `?tenantId=` on resource detail GETs).
 * Without it the new tag is created under the caller's own tenant, which
 * mismatches the resource's tenant and breaks any subsequent attach.
 */
export function createTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  body: TagCreateBody,
  tenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TagApiRow>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.post<TagsApiEnvelope<TagApiRow>>(
    `${pathPrefix}/tags`,
    body,
    mergeConfig(mount, { params: stripUndefined({ tenantId }) })
  );
}

/**
 * GET `<prefix>/tags/:id` — single tag with per-module resource counts. §3.3.
 *
 * `tenantId` semantics — see {@link GetTagsListParams}.
 */
export function getTagById(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  id: number,
  tenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TagApiRow>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.get<TagsApiEnvelope<TagApiRow>>(
    `${pathPrefix}/tags/${id}`,
    mergeConfig(mount, { params: stripUndefined({ tenantId }) })
  );
}

/**
 * PATCH `<prefix>/tags/:id` — partial update. §3.4.
 *
 * `tenantId` semantics — see {@link GetTagsListParams}.
 */
export function updateTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  id: number,
  body: TagPatchBody,
  tenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TagApiRow>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.patch<TagsApiEnvelope<TagApiRow>>(
    `${pathPrefix}/tags/${id}`,
    body,
    mergeConfig(mount, { params: stripUndefined({ tenantId }) })
  );
}

/**
 * DELETE `<prefix>/tags/:id` — hard-delete; **refused with 409 if `resourceCount > 0`**. §3.5.
 * Detach all resources first via `PUT /tags/:id/resources { resources: [] }`, then call DELETE.
 *
 * `tenantId` semantics — see {@link GetTagsListParams}.
 */
export function deleteTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  id: number,
  tenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TagDeleteData>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.delete<TagsApiEnvelope<TagDeleteData>>(
    `${pathPrefix}/tags/${id}`,
    mergeConfig(mount, { params: stripUndefined({ tenantId }) })
  );
}

// ---------------------------------------------------------------------------
// §4 Resources managed from the tag
// ---------------------------------------------------------------------------

export interface GetTagResourcesListParams {
  module?: TagsApiModule;
  resourceType?: string;
  limit?: number;
  offset?: number;
  /** Tenant override — see {@link GetTagsListParams}. */
  tenantId?: number;
}

/**
 * GET `<prefix>/tags/:id/resources` — resources linked to a tag. §4.1.
 */
export function getTagResourcesList(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: number,
  params?: GetTagResourcesListParams
): Promise<AxiosResponse<TagsApiEnvelope<TaggedResourceListPage>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.get<TagsApiEnvelope<TaggedResourceListPage>>(
    `${pathPrefix}/tags/${tagId}/resources`,
    {
      ...mergeConfig(mount, { params: stripUndefined({ ...params }) }),
    }
  );
}

/**
 * PUT `<prefix>/tags/:id/resources` — full replace of resource assignments. §4.2.
 * Body can mix resources from any module regardless of which prefix is used.
 * `resourceLabel` is server-computed — do NOT send it.
 *
 * `tenantId` semantics — see {@link GetTagsListParams}.
 */
export function updateTagResources(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: number,
  body: TagResourceReplaceBody,
  tenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TaggedResourceListPage>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.put<TagsApiEnvelope<TaggedResourceListPage>>(
    `${pathPrefix}/tags/${tagId}/resources`,
    body,
    mergeConfig(mount, { params: stripUndefined({ tenantId }) })
  );
}

// ---------------------------------------------------------------------------
// §5 Resources managed from the resource
// ---------------------------------------------------------------------------

/**
 * GET `<prefix>/resources/:resourceType/:resourceId/tags` — tags on one resource. §5.1.
 *
 * The optional `tenantId` query param overrides the tenant derived from the
 * JWT — required when a Global Admin or Partner is acting on behalf of a
 * child tenant (matches how `fetchPort` and the rest of the resource detail
 * GETs pass `?tenantId=`). Without it the backend resolves the tenant from
 * the caller's own JWT and returns 404 for cross-tenant resources.
 *
 * `asTenantId` is the tenant the caller is *acting as* (the SideNav lens
 * selection): the partner's own id in Partner Account view, the customer's
 * id in Customer view. While `tenantId` scopes the resource lookup (its
 * owner), `asTenantId` scopes WHICH tags the caller manages/sees, so a
 * partner only touches partner-owned tags and a customer only their own —
 * even on a shared resource. The backend keys read/write scope off it.
 */
export function getResourceTags(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  resourceType: string,
  resourceId: number,
  tenantId?: number,
  asTenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TagSummaryApiRow[]>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.get<TagsApiEnvelope<TagSummaryApiRow[]>>(
    `${pathPrefix}/resources/${encodeURIComponent(resourceType)}/${resourceId}/tags`,
    mergeConfig(mount, { params: stripUndefined({ tenantId, asTenantId }) })
  );
}

/**
 * PUT `<prefix>/resources/:resourceType/:resourceId/tags` — full replace tag ids on a resource. §5.2.
 *
 * `tenantId` semantics — see {@link getResourceTags}.
 */
export function updateResourceTags(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  resourceType: string,
  resourceId: number,
  body: ResourceTagsReplaceBody,
  tenantId?: number,
  asTenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<TagSummaryApiRow[]>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.put<TagsApiEnvelope<TagSummaryApiRow[]>>(
    `${pathPrefix}/resources/${encodeURIComponent(resourceType)}/${resourceId}/tags`,
    body,
    mergeConfig(mount, { params: stripUndefined({ tenantId, asTenantId }) })
  );
}

/** Pivot row returned by the atomic attach endpoint (§5.3). */
export interface AttachedResourceTagApiRow {
  id: number;
  tagId: number;
  module: TagsApiModule;
  resourceType: string;
  resourceId: number;
  resourceLabel: string | null;
  createdAt: string;
}

/** Body of the atomic detach response (§5.4). */
export interface DetachedResourceTagApiData {
  tagId: number;
  resourceId: number;
  detached: boolean;
}

/**
 * POST `<prefix>/resources/:resourceType/:resourceId/tags/:tagId` — atomic
 * attach. §5.3. Idempotent: 201 the first time, 200 if the tag was already
 * attached. Both responses carry the `TaggedResource` DTO under `data`.
 *
 * `tenantId` semantics — see {@link getResourceTags}.
 */
export function attachResourceTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  resourceType: string,
  resourceId: number,
  tagId: number,
  tenantId?: number,
  asTenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<AttachedResourceTagApiRow>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.post<TagsApiEnvelope<AttachedResourceTagApiRow>>(
    `${pathPrefix}/resources/${encodeURIComponent(resourceType)}/${resourceId}/tags/${tagId}`,
    undefined,
    mergeConfig(mount, { params: stripUndefined({ tenantId, asTenantId }) })
  );
}

/**
 * DELETE `<prefix>/resources/:resourceType/:resourceId/tags/:tagId` — atomic
 * detach. §5.4. Idempotent: 200 in both cases, with `data.detached` flagging
 * whether a row was actually removed.
 *
 * `tenantId` semantics — see {@link getResourceTags}.
 */
export function detachResourceTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  resourceType: string,
  resourceId: number,
  tagId: number,
  tenantId?: number,
  asTenantId?: number
): Promise<AxiosResponse<TagsApiEnvelope<DetachedResourceTagApiData>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.delete<TagsApiEnvelope<DetachedResourceTagApiData>>(
    `${pathPrefix}/resources/${encodeURIComponent(resourceType)}/${resourceId}/tags/${tagId}`,
    mergeConfig(mount, { params: stripUndefined({ tenantId, asTenantId }) })
  );
}

/** Lightweight item returned by the picker endpoint (§3.9). */
export interface TagPickerApiRow {
  id: number;
  name: string;
  color: string | null;
}

export interface TagPickerListPage {
  items: TagPickerApiRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetTagsPickerParams {
  /** Case-insensitive partial match on `name`. */
  search?: string;
  /**
   * When set together with {@link excludeForResourceId}, the response excludes
   * tags already attached to that resource so the picker only shows
   * attach-able candidates. Both params must be present or both omitted.
   */
  excludeForResourceType?: string;
  excludeForResourceId?: number;
  /** Default 20, max 100. */
  limit?: number;
  /** Default 0. */
  offset?: number;
  /**
   * Override the tenant derived from the JWT — required when a Global Admin
   * or Partner is acting on behalf of a child tenant. Without it the picker
   * lists tags from the caller's own tenant, which is disjoint from the
   * resource's tenant when the two differ.
   */
  tenantId?: number;
  /**
   * When `"self"`, lists ONLY tags owned by the acting tenant (no hierarchy
   * expansion, no ancestor widening). The resource-side modal uses this so
   * Partner Account view offers partner-owned tags only and Customer view
   * offers that customer's tags only — matching the SideNav dropdown lens.
   */
  scope?: "self";
}

/**
 * GET `<prefix>/tags/picker` — modal-sized tag list for "add tag from a
 * resource detail page" pickers. §3.9.
 *
 * Response shape is the slim `{ id, name, color }` per item — no slug,
 * description, category, resourceCount or timestamps. Designed to render
 * a colored chip with the tag name and nothing else.
 */
export function getTagsPicker(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  params?: GetTagsPickerParams
): Promise<AxiosResponse<TagsApiEnvelope<TagPickerListPage>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.get<TagsApiEnvelope<TagPickerListPage>>(
    `${pathPrefix}/tags/picker`,
    {
      ...mergeConfig(mount, { params: stripUndefined({ ...params }) }),
    }
  );
}

// ---------------------------------------------------------------------------
// §6 Catalog (picker)
// ---------------------------------------------------------------------------

export interface GetTagCatalogResourcesListParams {
  module: TagsApiModule;
  resourceType: string;
  search?: string;
  limit?: number;
  offset?: number;
  /** Tag id — exclude resources already tagged with this tag (use in edit flow). */
  excludeTaggedBy?: number;
  /** Tenant override — see {@link GetTagsListParams}. */
  tenantId?: number;
}

/**
 * GET `<prefix>/tags/catalog/resources` — picker catalog (module + resourceType required). §6.
 *
 * There is **no** companion "list all catalog `resourceType` values" call in this
 * repo: the dashboard keeps the allowlist-aligned map in
 * `TagsManager/requests/productAdapters.ts` (`resolveTagsCatalogListParams`).
 * (By contrast, Audit Logs loads dynamic resource types from
 * `GET /api/v2/audit-logs/filters/options` — a different product API.)
 */
export function getTagCatalogResourcesList(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  params: GetTagCatalogResourcesListParams
): Promise<AxiosResponse<TagsApiEnvelope<CatalogListPage>>> {
  const { pathPrefix } = tagsMountConfig(mount);
  return api.get<TagsApiEnvelope<CatalogListPage>>(
    `${pathPrefix}/tags/catalog/resources`,
    {
      ...mergeConfig(mount, { params: stripUndefined({ ...params }) }),
    }
  );
}
